import { getHeaders } from "../request-headers.ts";
import type {
  CurrentUserResponse,
  GetMyReferralsParams,
  OkResponse,
  RecoverPasswordRequest,
  ReferralsResponse,
  ResetPasswordRequest,
  SendVerificationCodeRequest,
  SetUsernameRequest,
  SetUsernameResponse,
  SignInRequest,
  SignInResponse,
  WechatReviewSubscriptionStatusResponse,
} from "../../model";

import { cnapexApiFetch } from "../../../fetcher.ts";

export const getCancelAccountUrl = () => {
  return `/api/auth/cancel-account`;
};

export const cancelAccount = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getCancelAccountUrl(), {
    ...options,
    method: "POST",
  });
};

export const getGetCurrentUserUrl = () => {
  return `/api/auth/current-user`;
};

export const getCurrentUser = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<CurrentUserResponse> => {
  return cnapexApiFetch<CurrentUserResponse>(getGetCurrentUserUrl(), {
    ...options,
    method: "GET",
  });
};

export const getRecoverPasswordUrl = () => {
  return `/api/auth/recover-password`;
};

export const recoverPassword = async (
  recoverPasswordRequest: RecoverPasswordRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getRecoverPasswordUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(recoverPasswordRequest),
  });
};

export const getResetPasswordUrl = () => {
  return `/api/auth/reset-password`;
};

export const resetPassword = async (
  resetPasswordRequest: ResetPasswordRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getResetPasswordUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(resetPasswordRequest),
  });
};

export const getSignInUrl = () => {
  return `/api/auth/sign-in`;
};

export const signIn = async (
  signInRequest: SignInRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<SignInResponse> => {
  return cnapexApiFetch<SignInResponse>(getSignInUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(signInRequest),
  });
};

export const getSignOutUrl = () => {
  return `/api/auth/sign-out`;
};

export const signOut = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getSignOutUrl(), {
    ...options,
    method: "POST",
  });
};

export const getSendVerificationCodeUrl = () => {
  return `/api/auth/verification-code`;
};

export const sendVerificationCode = async (
  sendVerificationCodeRequest: SendVerificationCodeRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getSendVerificationCodeUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(sendVerificationCodeRequest),
  });
};

export const getSetUsernameUrl = () => {
  return `/api/users/me/name`;
};

export const setUsername = async (
  setUsernameRequest: SetUsernameRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<SetUsernameResponse> => {
  return cnapexApiFetch<SetUsernameResponse>(getSetUsernameUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(setUsernameRequest),
  });
};

export const getGetMyReferralsUrl = (params?: GetMyReferralsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/users/me/referrals?${stringifiedParams}`
    : `/api/users/me/referrals`;
};

export const getMyReferrals = async (
  params?: GetMyReferralsParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<ReferralsResponse> => {
  return cnapexApiFetch<ReferralsResponse>(getGetMyReferralsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getGetWechatReviewSubscriptionStatusUrl = () => {
  return `/api/users/me/wechat-review-subscription-status`;
};

export const getWechatReviewSubscriptionStatus = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<WechatReviewSubscriptionStatusResponse> => {
  return cnapexApiFetch<WechatReviewSubscriptionStatusResponse>(
    getGetWechatReviewSubscriptionStatusUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};
